// Options (legacy window/options.js): tabs Scoring / Settings / Language; every click saves and
// applies at once. Opens on Settings. A language change asks for a reload (Confirm window on
// desktop, native confirm on mobile); Cancel reverts the saved value (Q-OPT-3 fixed).
import { effectsOnFor, oldBrowser } from '../ui/effects';
import { env } from '../env';
import { LANGUAGE_TAB_ORDER, currentLanguage, languageName, t, type LanguageCode } from '../i18n';
import type { GameOptionValues } from '../options/storage';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { Tabs, WindowFrame } from './WindowFrame';

type Key = keyof GameOptionValues;

/** `current`: the value shown as chosen when it differs from the stored one (a default that follows another option). */
function Radio<K extends Key>({ id, items, current: shown }: { id: K; items: Array<{ value: GameOptionValues[K] & string; label: string }>; current?: string }) {
  const ui = useUiState();
  const current = shown ?? ui.options.option[id];
  return (
    <div className="option_item radio">
      {items.map((it) => (
        <a
          key={it.value}
          href="#"
          className={`option_item_entry${current === it.value ? ' selected' : ''}`}
          data-item={`${id}|${it.value}`}
          onClick={(e) => {
            e.preventDefault();
            uiStore().setOption(id, it.value);
          }}
        >
          {it.label}
        </a>
      ))}
    </div>
  );
}

function Checkbox({ id, label }: { id: 'autoFlip' | 'showTime' | 'showScore' | 'showMoves' | 'focusMode' | 'dockHidden' | 'wasteFan' | 'noMovesNotice'; label: string }) {
  const ui = useUiState();
  const on = ui.options.option[id];
  return (
    <div className="option_item checkbox">
      <a
        href="#"
        className={`option_item_entry${on ? ' selected' : ''}`}
        data-item={`${id}|`}
        onClick={(e) => {
          e.preventDefault();
          uiStore().setOption(id, !on);
        }}
      >
        {label}
      </a>
    </div>
  );
}

/** The classic mode (owner 2026-10-23: "Tryb klasyczny"): the 3D effects off (ui/effects.ts) and the legacy
 *  motion (engine/motion.ts). Ticked = classic; shows what is in use (a slow device: classic by itself); a click
 *  stores the choice. Stored as `effects: 'on' | 'off'` like the 3D checkbox it replaces. On an old browser
 *  (ui/effects.ts oldBrowser) it is ticked and greyed out, with a line that says why (owner 2026-10-25). */
function EffectsCheckbox() {
  const ui = useUiState();
  const classic = !effectsOnFor(ui.options.option.effects);
  const locked = oldBrowser();
  return (
    <div className="option_item checkbox">
      <a
        href="#"
        className={`option_item_entry${classic ? ' selected' : ''}${locked ? ' locked' : ''}`}
        data-item="effects|"
        aria-disabled={locked || undefined}
        onClick={(e) => {
          e.preventDefault();
          if (!locked) uiStore().setOption('effects', classic ? 'on' : 'off');
        }}
      >
        <span>
          {t('optionWindowTabSettingsClassic')}
          {locked && <span className="option_item_note">{t('optionWindowTabSettingsClassicLocked')}</span>}
        </span>
      </a>
    </div>
  );
}

/** The way back to the legacy game (owner 2026-10-27: players miss the old one): the last entry of the settings, under
 *  the Classic mode. Not an option of this game: the server's /switch-ui sets the cookie `ui=legacy-pick` and loads the
 *  same page again as the legacy game, whose Options have "New version of the game" (server/routes/uiSwitch.js). Only
 *  on the site itself (the partner and offline targets have no legacy page to go to). */
function LegacySwitch() {
  if (env.appTarget && env.appTarget !== 'self') return null;
  return (
    <div className="option_item link">
      <a href={`/switch-ui?to=legacy&back=${encodeURIComponent(location.pathname)}`} className="option_item_entry" data-item="ui|legacy">
        <span>
          {t('optionWindowTabSettingsLegacy')}
          <span className="option_item_note">{t('optionWindowTabSettingsLegacyNote')}</span>
        </span>
      </a>
    </div>
  );
}

/** Options "Column spacing": shows what is in use (not chosen: narrow in the Classic mode, wide with the 3D effects);
 *  a click stores the choice (`columns: 'wide' | 'narrow'`). */
function ColumnsRadio() {
  const ui = useUiState();
  const pref = ui.options.option.columns;
  const inUse = pref === 'auto' ? (effectsOnFor(ui.options.option.effects) ? 'wide' : 'narrow') : pref;
  return <Radio id="columns" current={inUse} items={[{ value: 'wide', label: t('optionWindowTabSettingsColumnsWide') }, { value: 'narrow', label: t('optionWindowTabSettingsColumnsNarrow') }]} />;
}

const Label = ({ text }: { text: string }) => (
  <div className="option_item label">
    <span className="header_label">{text}</span>
  </div>
);

function LanguageTab() {
  const ui = useUiState();
  const current = ui.options.option.lang || currentLanguage();
  const pick = (code: LanguageCode) => {
    const previous = ui.options.option.lang;
    uiStore().setOption('lang', code);
    if (code === currentLanguage()) return;
    const body = t('optionWindowTabSettingsLangLabel', { lang: languageName(code) });
    const reload = () => location.reload();
    const revert = () => uiStore().setOption('lang', previous);
    if (document.body.id === 'mobile') {
      if (window.confirm(body.replace(/<[^>]*>/g, ''))) reload();
      else revert();
      return;
    }
    uiStore().openWindow('confirm', { header: t('optionWindowTabLanguageHeader'), body, onOk: reload, onCancel: revert });
  };
  return (
    <div className="option_item radio">
      {LANGUAGE_TAB_ORDER.map((code) => (
        <a
          key={code}
          href="#"
          className={`option_item_entry${current === code ? ' selected' : ''}`}
          data-item={`lang|${code}`}
          onClick={(e) => {
            e.preventDefault();
            pick(code);
          }}
        >
          {languageName(code)}
        </a>
      ))}
    </div>
  );
}

export function OptionsWindow({ instance }: { instance: WindowInstance }) {
  const multiplayer = env.appTarget === 'multiplayer';
  const soundHeader = t('option.settings-sound-header') + (typeof document.createElement('audio').canPlayType === 'function' ? '' : ` (${t('option.settings-sound-header-not-supported')})`);
  const settings = (
    <>
      <Label text={t('optionWindowTabSettingsAutoHeader')} />
      <Checkbox id="autoFlip" label={t('optionWindowTabSettingsAutoFlip')} />
      <Radio id="autoMove" items={[{ value: 'when_won', label: t('optionWindowTabSettingsAutoMoveWhenWon') }, { value: 'off', label: t('optionWindowTabSettingsAutoMoveOff') }]} />
      {/* owner 2026-10-26: off by default (ui/lostGame.ts) */}
      <Checkbox id="noMovesNotice" label={t('optionWindowTabSettingsNoMovesNotice')} />
      <Label text={soundHeader} />
      <Radio id="sound" items={[{ value: 'paper', label: t('option.settings-sound-pack-paper') }, { value: 'plastic', label: t('option.settings-sound-pack-plastic') }, { value: 'off', label: t('option.settings-sound-off') }]} />
      {/* every game (owner 2026-10-24): not chosen, it follows the Classic mode (narrow there); under the sounds */}
      <Label text={t('optionWindowTabSettingsColumnsHeader')} />
      <ColumnsRadio />
      <Label text={t('optionWindowTabSettingsControlHeader')} />
      <Checkbox id="showTime" label={t('optionWindowTabSettingsControlTime')} />
      <Checkbox id="showScore" label={t('optionWindowTabSettingsControlScores')} />
      <Checkbox id="showMoves" label={t('optionWindowTabSettingsControlMoves')} />
      {/* the display group (owner 2026-10-23: the last entries stood under "Controls" with the counters) */}
      <Label text={t('optionWindowTabSettingsDisplayHeader')} />
      <Checkbox id="focusMode" label={t('optionWindowTabSettingsFocusMode')} />
      <Checkbox id="dockHidden" label={t('optionWindowTabSettingsHideDock')} />
      <Checkbox id="wasteFan" label={t('optionWindowTabSettingsWasteFan')} />
      <EffectsCheckbox />
      <LegacySwitch />
    </>
  );
  const scoring = <Radio id="scoring" items={[{ value: 'standard', label: t('scoringStandardMode') }, { value: 'timed', label: t('scoringTimedMode') }]} />;
  const labels = multiplayer ? [t('optionWindowTabSettingsHeader'), t('optionWindowTabLanguageHeader')] : [t('optionWindowTabScoringHeader'), t('optionWindowTabSettingsHeader'), t('optionWindowTabLanguageHeader')];
  const panes = multiplayer ? [settings, <LanguageTab key="lang" />] : [scoring, settings, <LanguageTab key="lang" />];
  return (
    <WindowFrame name="options" header={t('optionWindowHeader')} closing={instance.closing}>
      <div className="options_window">
        <Tabs labels={labels} initial={multiplayer ? 0 : 1}>
          {panes}
        </Tabs>
      </div>
    </WindowFrame>
  );
}
